// =============================================================================
// TrackerPage.jsx
//
// Alterações relativamente à v1:
//   1. Estado de filtros (filters) — filtragem puramente local, sem chamadas API
//   2. useMemo → filteredEntries — entries após aplicar todos os filtros activos
//   3. Renderiza TrackerFilters acima da tabela
//   4. Renderiza TrackerGantt abaixo da tabela
//
// Lógica de filtragem temporal:
//   From → StartDate >= From
//   To   → StartDate <= To
// =============================================================================

import React, { useState, useEffect, useReducer, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Typography, Alert, Snackbar,
  Stack, Chip, CircularProgress, Tooltip, IconButton,
  FormControlLabel, Switch,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import TuneIcon           from '@mui/icons-material/Tune';
import DownloadIcon       from '@mui/icons-material/Download';
import TimelineIcon       from '@mui/icons-material/Timeline';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';

import TrackerTable    from './TrackerTable';
import TrackerModal    from './TrackerModal';
import SiteListDialog  from './SiteListDialog';
import TrackerFilters  from './TrackerFilters';
import TrackerGantt    from './TrackerGantt';
import TrackerCalendar  from './TrackerCalendar';
import TrackerSettings  from './TrackerSettings';

import trackerReducer                                  from '../reducer/trackerReducer';
import { loadEntries, loadEntry, saveEntry, removeEntry } from '../reducer/trackerThunks';
import { openModal, closeModal, clearError }           from '../actions/trackerActions';
import { apiExportAll, apiGetFeatures, apiGetPrivilege } from '../requests/trackerRequests';


// =============================================================================
// useThunkReducer — suporta thunks (funções) além de actions (objectos)
// =============================================================================
const useThunkReducer = (reducer, initialState) => {
  const [state, rawDispatch] = useReducer(reducer, initialState);

  const dispatch = useCallback(
    (action) => {
      if (typeof action === 'function') {
        action(dispatch);
      } else {
        rawDispatch(action);
      }
    },
    [rawDispatch]
  );

  return [state, dispatch];
};


// ── State inicial do reducer ──────────────────────────────────────────────────
const initialState = {
  entries:     [],
  activeEntry: null,
  loading:     false,
  saving:      false,
  loadingOne:  false,
  error:       null,
  modalOpen:   false,
};

// ── State inicial dos filtros ─────────────────────────────────────────────────
const emptyFilters = {
  dateFrom:     null,   // 'yyyy-MM-dd' ou null
  dateTo:       null,   // 'yyyy-MM-dd' ou null
  technologies: [],     // string[]
  teams:        [],     // string[]
  owners:       [],     // string[]
  features:     [],     // string[]
  vendors:      [],     // string[]
};

const initialSnackbar = { open: false, message: '', severity: 'success' };


// =============================================================================
// Helpers de filtragem
// =============================================================================

// Converte 'yyyy-MM-dd' → timestamp (início do dia UTC)
const toMs = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00Z').getTime() : null;

// Converte string de data da API (vários formatos) → timestamp UTC
const parseDateMs = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime();

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T00:00:00Z`).getTime();
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Aplica todos os filtros activos a um array de entries
const applyFilters = (entries, filters) => {
  const filtFrom = toMs(filters.dateFrom);
  const filtTo   = toMs(filters.dateTo);

  return entries.filter((e) => {
    // ── Filtro de datas ───────────────────────────────────────────────────────
    if (filtFrom || filtTo) {
      const actStart = parseDateMs(e.startDate);

      if (!actStart) return false;

      // From → StartDate >= From
      if (filtFrom && actStart < filtFrom) return false;

      // To   → StartDate <= To
      if (filtTo && actStart > filtTo) return false;
    }

    // ── Filtro de tecnologia ──────────────────────────────────────────────────
    if (filters.technologies.length > 0) {
      if (!filters.technologies.includes(e.technology)) return false;
    }

    // ── Filtro de equipa ──────────────────────────────────────────────────────
    if (filters.teams.length > 0) {
      if (!filters.teams.includes(e.teamName)) return false;
    }

    // ── Filtro de owner ───────────────────────────────────────────────────────
    if (filters.owners.length > 0) {
      const entryOwners = e.ownerNames ?? [];
      const hasMatch = entryOwners.some((o) => filters.owners.includes(o));
      if (!hasMatch) return false;
    }

    // ── Filtro de feature ─────────────────────────────────────────────────────
    if (filters.features.length > 0) {
      console.log('[Filter] e.feature:', JSON.stringify(e.feature), '| filters.features:', JSON.stringify(filters.features), '| match:', filters.features.includes(e.feature));
      if (!filters.features.includes(e.feature)) return false;
    }

    if (filters.vendors.length > 0) {
      if (!filters.vendors.includes(e.vendor)) return false;
    }

    return true;
  });
};


// =============================================================================
// COMPONENTE
// =============================================================================
const TrackerPage = () => {
  const [state, dispatch] = useThunkReducer(trackerReducer, initialState);
  const { entries, activeEntry, loading, saving, loadingOne, error, modalOpen } = state;

  // userId do utilizador autenticado — enviado para a API para o audit log
  const userId = useSelector((s) => s.session.user.id);

  // ── Privilege (role + vendor + module access) ─────────────────────────────
  const [privilege, setPrivilege] = useState(null);   // null = still loading
  const role      = privilege?.role ?? null;
  const canCreate    = role === null || role === 'Admin' || role === 'Regular';
  const canDelete    = role === null || role === 'Admin';
  const canEditEntry = (entry) => role === null || role === 'Admin' || (role === 'Regular' && entry?.createdBy === userId);
  const vendor       = privilege?.vendor ?? 'Both';

  const [snackbar, setSnackbar] = useState(initialSnackbar);
  const [filters,  setFilters]  = useState(emptyFilters);
  const [showClosed, setShowClosed] = useState(false);
  const [featureTypes, setFeatureTypes] = useState([]);  // [{ id, name, color }]
  const [chartView, setChartView] = useState('gantt');   // 'gantt' | 'calendar'
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [lookupsVersion, setLookupsVersion] = useState(0);  // incremented when Settings closes

  useEffect(() => {
    apiGetFeatures()
      .then((res) => setFeatureTypes(res.data ?? []))
      .catch(() => {});
  }, []);

  // ── SiteListDialog — fora do TrackerModal para evitar bug de backdrops ────
  const [siteListOpen,     setSiteListOpen]     = useState(false);
  const [siteListServer,   setSiteListServer]   = useState(null);
  const [siteListCallback, setSiteListCallback] = useState(null);


  // ── Entries filtradas (memo — só recalcula quando entries ou filters mudam) ─
  const filteredEntries = useMemo(
    () => applyFilters(entries, filters),
    [entries, filters]
  );


  // ── Snackbar helpers ──────────────────────────────────────────────────────
  const showSnack  = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const closeSnack = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));


  // ── Carregar tabela ao montar ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadEntries(vendor));

    // Fetch privilege for this user
    if (userId) {
      apiGetPrivilege(userId)
        .then((res) => {
          const data = res.data;
          if (!data) { setPrivilege({ role: 'Tourist', vendor: 'Both' }); return; }
          const hasAccess = data.moduleAccess?.find((m) => m.moduleKey === 'activitiesTracker')?.hasAccess;
          setPrivilege({
            role:   hasAccess ? (data.role ?? 'Tourist') : 'Tourist',
            vendor: data.vendor ?? 'Both',
          });
        })
        .catch(() => setPrivilege({ role: 'Tourist', vendor: 'Both' }));
    }
  }, []);


  // ── Snackbar após operações de escrita ────────────────────────────────────
  const prevSaving     = useRef(false);
  const lastOpIsDelete = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving && !error) {
      showSnack(
        lastOpIsDelete.current ? 'Activity deleted.' : 'Activity saved successfully.',
        lastOpIsDelete.current ? 'info' : 'success'
      );
      lastOpIsDelete.current = false;
    }
    prevSaving.current = saving;
  }, [saving, error]);  // eslint-disable-line react-hooks/exhaustive-deps


  // ── Handlers da tabela e modal ────────────────────────────────────────────

  const handleRowClick = (row) => {
    const normalizedRow = {
      ...row,
      teamId:  row.teamId  != null ? String(row.teamId)  : '',
      ownerId: row.ownerId != null ? String(row.ownerId) : '',
    };
    dispatch(openModal(normalizedRow));
    dispatch(loadEntry(row.id));
  };

  const handleAddNew     = ()         => dispatch(openModal(null));

  const handleCopy = (entry) => {
    dispatch(openModal({
      ...entry,
      id:        undefined,
      title:     `${entry.title} - Copy`,
      startDate: '',
      endDate:   '',
      phases:    [],
      comments:  '',
      status:    'Open',
    }));
  };
  const handleCloseModal = ()         => dispatch(closeModal());
  const handleSave = (formData) => {
    lastOpIsDelete.current = false;
    dispatch(saveEntry(formData, userId));
  };

  const handleDeleteFromTable = (id, entryTitle) => {
    if (!window.confirm('Remove this activity? This cannot be undone.')) return;
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };

  const handleDeleteFromModal = (id, entryTitle) => {
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };


  // ── Handlers do SiteListDialog ────────────────────────────────────────────

  const handleOpenSiteList = (server, updateServerFn) => {
    setSiteListServer(server);
    setSiteListCallback(() => updateServerFn);
    setSiteListOpen(true);
  };

  const handleCloseSiteList = () => {
    setSiteListOpen(false);
    setTimeout(() => {
      setSiteListServer(null);
      setSiteListCallback(null);
    }, 300);
  };

  const handleConfirmSiteList = (selectedSites) => {
    if (siteListCallback) siteListCallback(selectedSites);
    handleCloseSiteList();
  };


  // ── Chips de contagem por tecnologia (sobre as entries filtradas) ─────────
  const techCounts = ['2G', '3G', '4G', '5G']
    .map((t) => ({ t, n: filteredEntries.filter((e) => e.technology === t && (showClosed || e.status !== 'Closed')).length }))
    .filter((x) => x.n > 0);

  const displayedEntries = showClosed ? filteredEntries : filteredEntries.filter((e) => e.status !== 'Closed');
  const isFiltered = displayedEntries.length < (showClosed ? entries : entries.filter(e => e.status !== 'Closed')).length;


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} letterSpacing={-0.5} color="text.primary">
            Network Activity Tracker
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {isFiltered
              ? <><strong>{displayedEntries.length}</strong> of {(showClosed ? entries : entries.filter(e => e.status !== 'Closed')).length} activities · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
              : <>{displayedEntries.length} {displayedEntries.length === 1 ? 'activity' : 'activities'} · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
            }
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {loadingOne && <CircularProgress size={20} />}
          <FormControlLabel
            control={<Switch size="small" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />}
            label={<Typography variant="caption" color="text.secondary">Show Closed</Typography>}
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          <Tooltip title="Export all activities to Excel">
            <span>
              <Button
                variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => apiExportAll()}
                disabled={entries.length === 0}
                disableElevation
                size="small"

              >
                Export All
              </Button>
            </span>
          </Tooltip>
          {(role === 'Admin' || role === 'Manager') && (
            <Button
              variant="outlined" startIcon={<TuneIcon />}
              onClick={() => setSettingsOpen(true)}
              disableElevation size="small"
            >
              Settings
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} disableElevation size="small"
            disabled={!canCreate}
            title={!canCreate ? 'You do not have permission to create activities' : undefined}
          >
            New Activity
          </Button>
        </Box>
      </Stack>

      {/* ── Banner de erro global ───────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* ── Barra de filtros ────────────────────────────────────────────────── */}
      <TrackerFilters
        entries={entries}
        filters={filters}
        onChange={setFilters}
        onClear={() => setFilters(emptyFilters)}
        filtered={filteredEntries.length}
      />

      {/* ── Tabela (recebe as entries JÁ filtradas) ─────────────────────────── */}
      <TrackerTable
        entries={filteredEntries}
        loading={loading || loadingOne}
        onRowClick={handleRowClick}
        onDelete={handleDeleteFromTable}
        onCopy={handleCopy}
        showClosed={showClosed}
        canEditEntry={canEditEntry}
        canDelete={canDelete}
        userRole={role}
        userId={userId}
      />

      {/* ── Divisor entre tabela e Gantt/Calendar ───────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 0, gap: 2 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {chartView === 'gantt' ? 'Gantt Chart' : 'Calendar View'}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* ── Toggle Gantt / Calendar ──────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, mb: -1.5, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: '8px', p: '3px', gap: '2px' }}>
          {[
            { value: 'gantt',    icon: <TimelineIcon sx={{ fontSize: 13 }} />,      label: 'Gantt' },
            { value: 'calendar', icon: <CalendarMonthIcon sx={{ fontSize: 13 }} />, label: 'Calendar' },
          ].map(({ value, icon, label }) => (
            <Box
              key={value}
              onClick={() => setChartView(value)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.4, py: 0.45, borderRadius: '6px',
                cursor: 'pointer', userSelect: 'none',
                transition: 'all 0.15s ease',
                color: chartView === value ? 'primary.main' : 'text.secondary',
                bgcolor: chartView === value ? '#ffffff' : 'transparent',
                boxShadow: chartView === value ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {icon}
              <Typography variant="button" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>


      {/* ── Legenda partilhada (Gantt + Calendar) ──────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        px: 0.5, py: 1,
      }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4E79A7', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Feature
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#59A14F', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Optimisation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#E07B39', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Parameter Workbook Rollout
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8B6BB1', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Software Upgrade
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#E15759', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Service Protection Period
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e1df57', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              Maintenance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9BA3AF', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Other</Typography>
          </Box>
      </Box>
      {chartView === 'gantt' ? (
        <TrackerGantt
          entries={filteredEntries}
          onRowClick={handleRowClick}
          showClosed={showClosed}
          featureTypes={featureTypes}
        />
      ) : (
        <TrackerCalendar
          entries={filteredEntries}
          featureTypes={featureTypes}
          onRowClick={handleRowClick}
          showClosed={showClosed}
        />
      )}

      {/* ── Modal de criar/editar ───────────────────────────────────────────── */}
      <TrackerModal
        key={lookupsVersion}
        open={modalOpen}
        entry={activeEntry}
        saving={saving}
        loadingOne={loadingOne}
        onSave={handleSave}
        onClose={handleCloseModal}
        onDelete={handleDeleteFromModal}
        onOpenSiteList={handleOpenSiteList}
        userRole={role}
        userId={userId}
        canDelete={canDelete}
        privilegeVendor={vendor}
      />

      <TrackerSettings
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setLookupsVersion((v) => v + 1); }}
      />

      {/*
        SiteListDialog renderizado aqui, ao lado do TrackerModal — nunca dentro.
        Assim os dois Dialogs são independentes no DOM e o fecho de um
        nunca afecta o outro.
      */}
      <SiteListDialog
        open={siteListOpen}
        server={siteListServer}
        onClose={handleCloseSiteList}
        onConfirm={handleConfirmSiteList}
      />

      {/* ── Snackbar de feedback ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TrackerPage;